import React, { useState, useEffect } from 'react';
import { 
  Server, 
  Play, 
  Square, 
  Trash2, 
  Share2, 
  AlertTriangle, 
  X, 
  CheckCircle, 
  Loader,
  ExternalLink,
  Users,
  HardDrive,
  SquareTerminal,
  Cpu,
  Layers2,
  Box
} from 'lucide-react';
import BlenderServerManager from './BlenderServerManager';
import FreeCADServerManager from './FreeCADServerManager';
import JupyterLabServerManager from './JupyterLabServerManager';
import KiCadServerManager from './KiCadServerManager';
import StorageManager from './StorageManager';
import styles from './ManageServersModal.module.css';

// Optional ViewerContext usage
let useViewerContext;
try {
  const viewerContext = require('../../context/ViewerContext');
  useViewerContext = viewerContext.useViewerContext;
} catch (error) {
  // Fallback when ViewerContext is not available
  useViewerContext = () => ({ openFile: () => {} });
}

function ManageServersModal({ onClose, title }) {
  const [activeTab, setActiveTab] = useState('jupyterlab');
  const [lastServerTab, setLastServerTab] = useState('jupyterlab'); // Track last active server tab
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedServer, setSelectedServer] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  
  // Safely get openFile from context
  let openFile;
  try {
    const viewerContext = useViewerContext();
    openFile = viewerContext.openFile;
  } catch (error) {
    // Fallback when context is not available
    openFile = () => {};
  }

  // Update last server tab when switching between server tabs
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'blender' || newTab === 'freecad' || newTab === 'jupyterlab' || newTab === 'kicad') {
      setLastServerTab(newTab);
    }
  };


  // Fetch servers on component mount
  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/v1/eks_jupyterlab/list', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        setServers(data.data.servers || []);
      } else {
        setError(data.retmsg || 'Failed to fetch servers');
      }
    } catch (err) {
      console.error('Error fetching servers:', err);
      setError('Failed to fetch servers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createServer = async () => {
    setActionLoading({ ...actionLoading, create: true });
    setError('');
    
    try {
      const response = await fetch('/v1/eks_jupyterlab/create', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          server_name: `server-${Date.now()}`
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to create server');
      }
    } catch (err) {
      console.error('Error creating server:', err);
      setError('Failed to create server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, create: false });
    }
  };

  const startServer = async (serverName) => {
    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/start/${serverName}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to start server');
      }
    } catch (err) {
      console.error('Error starting server:', err);
      setError('Failed to start server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const stopServer = async (serverName) => {
    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/stop/${serverName}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to stop server');
      }
    } catch (err) {
      console.error('Error stopping server:', err);
      setError('Failed to stop server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const deleteServer = async (serverName) => {
    if (!confirm(`Are you sure you want to delete server "${serverName}"? This will permanently delete all data.`)) {
      return;
    }

    setActionLoading({ ...actionLoading, [serverName]: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/delete/${serverName}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to delete server');
      }
    } catch (err) {
      console.error('Error deleting server:', err);
      setError('Failed to delete server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, [serverName]: false });
    }
  };

  const shareServer = async () => {
    if (!shareUserId.trim()) {
      setError('Please enter a user ID to share with');
      return;
    }

    setActionLoading({ ...actionLoading, share: true });
    setError('');
    
    try {
      const response = await fetch(`/v1/eks_jupyterlab/share/${selectedServer}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target_user_id: shareUserId.trim()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.RetCode === 0) {
        setShowShareModal(false);
        setShareTenantId('');
        setSelectedServer(null);
        await fetchServers(); // Refresh the list
      } else {
        setError(data.retmsg || 'Failed to share server');
      }
    } catch (err) {
      console.error('Error sharing server:', err);
      setError('Failed to share server. Please try again.');
    } finally {
      setActionLoading({ ...actionLoading, share: false });
    }
  };

  const openJupyterLab = async (serverName) => {
    const server = servers.find(s => s.server_name === serverName);
    if (server && server.status === 'running') {
      try {
        const response = await fetch(`/v1/eks_jupyterlab/url/${serverName}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data.RetCode === 0) {
          openFile({
            id: `jupyterlab-${serverName}`,
            name: `JupyterLab: ${serverName}.jupyterlab`,
            type: 'jupyterlab',
            url: data.data.labUrl,
          });
        } else {
          setError(data.retmsg || 'Failed to get JupyterLab URL');
        }
      } catch (err) {
        console.error('Error getting JupyterLab URL:', err);
        setError('Failed to get JupyterLab URL. Please try again.');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'text-green-500';
      case 'stopped':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'stopped':
        return <Square className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Server className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Use custom title if provided, else fallback
  const modalTitle = title || 'Manage Development Servers';

  const renderTabBar = () => (
    <div className={styles.tabBar}>
      <button
        onClick={() => handleTabChange('blender')}
        className={
          activeTab === 'blender'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
        style={{ minWidth: '140px', justifyContent: 'center', letterSpacing: '0.01em' }}
      >
        <Layers2 className="h-5 w-5 mr-2" />
        Blender
      </button>
      <button
        onClick={() => handleTabChange('freecad')}
        className={
          activeTab === 'freecad'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
        style={{ minWidth: '140px', justifyContent: 'center', letterSpacing: '0.01em' }}
      >
        <Box className="h-5 w-5 mr-2" />
        FreeCAD
      </button>
      <button
        onClick={() => handleTabChange('jupyterlab')}
        className={
          activeTab === 'jupyterlab'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
        style={{ minWidth: '140px', justifyContent: 'center', letterSpacing: '0.01em' }}
      >
        <SquareTerminal className="h-5 w-5 mr-2" />
        JupyterLab
      </button>
      <button
        onClick={() => handleTabChange('kicad')}
        className={
          activeTab === 'kicad'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
        style={{ minWidth: '140px', justifyContent: 'center', letterSpacing: '0.01em' }}
      >
        <Cpu className="h-5 w-5 mr-2" />
        KiCad
      </button>
      <button
        onClick={() => handleTabChange('storage')}
        className={
          activeTab === 'storage'
            ? `${styles.tabButton} ${styles.tabButtonActive}`
            : styles.tabButton
        }
        style={{ minWidth: '140px', justifyContent: 'center', letterSpacing: '0.01em' }}
      >
        <HardDrive className="h-5 w-5 mr-2" />
        Storage
      </button>
      {/* Future: add more server type tabs here */}
    </div>
  );

  const modalContent = (
    <div className={`w-full max-w-6xl ${styles.modal} max-h-[80vh] flex flex-col`}>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <div className={styles.headerIcon}>
              <Server className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">{modalTitle}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {renderTabBar()}
      </div>
      <p className={styles.info}>
        {activeTab === 'blender' 
          ? 'Create, start, stop, share or delete your Blender servers.'
          : activeTab === 'freecad'
          ? 'Create, start, stop, share or delete your FreeCAD servers.'
          : activeTab === 'jupyterlab'
          ? 'Create, start, stop, share or delete your JupyterLab servers.'
          : activeTab === 'kicad'
          ? 'Create, start, stop, share or delete your KiCad servers.'
          : 'Manage your persistent storage independently of servers.'
        }
      </p>
      {/* Tab content */}
      {activeTab === 'blender' && (
        <div className={styles.serverList}>
          <BlenderServerManager />
        </div>
      )}
      {activeTab === 'freecad' && (
        <div className={styles.serverList}>
          <FreeCADServerManager />
        </div>
      )}
      {activeTab === 'jupyterlab' && (
        <div className={styles.serverList}>
          <JupyterLabServerManager />
        </div>
      )}
      {activeTab === 'kicad' && (
        <div className={styles.serverList}>
          <KiCadServerManager />
        </div>
      )}
      {activeTab === 'storage' && (
        <div className={styles.serverList}>
          <StorageManager appType="jupyterlab" />
        </div>
      )}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      {modalContent}
    </div>
  );
}
export default ManageServersModal; 